import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  Checkbox,
  Chip,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useQuery, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints, IMAGE_URL } from "src/network/endpoints";
import { toast } from "react-hot-toast";

// styled components for better UI
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(2),
    maxHeight: "80vh",
    width: "100%",
    maxWidth: "500px",
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const UserListContainer = styled(Box)(({ theme }) => ({
  maxHeight: "400px",
  overflowY: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
}));

const SelectedUsersContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  minHeight: "40px",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  alignItems: "center",
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.grey[50],
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(3),
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  color: theme.palette.text.secondary,
}));

/**
 * AddMemberDialog component for adding members to a group
 * Features:
 * - Search functionality to find users
 * - Multi-select users to add
 * - Real-time search with debouncing
 * - Location-based user fetching
 * - Proper error handling and loading states
 */
const AddMemberDialog = ({
  open,
  onClose,
  conversationId,
  currentMembers = [],
  onAddMembers,
  // Sub-conversation specific props
  isSubConversation = false,
  subConversationData = null,
  onAddMembersToSubConversation,
}) => {
  // state for search functionality
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  
  // state for confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    user: null,
  });
  
  // get query client for cache invalidation
  const queryClient = useQueryClient();

  // get current user location for API call
  useEffect(() => {
    if (open && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        async (error) => {
          try {
            // fallback to IP-based location
            const response = await fetch(
              process.env.REACT_APP_LOCATION_URL || "https://ipapi.co/json/"
            );
            const data = await response.json();

            if (data?.latitude && data?.longitude) {
              setCurrentLocation({
                lat: data.latitude,
                lng: data.longitude,
              });
            } else {
              // default location (New Delhi, India)
              setCurrentLocation({ lat: 28.6139, lng: 77.209 });
            }
          } catch (ipError) {
            // use default location
            setCurrentLocation({ lat: 28.6139, lng: 77.209 });
          }
        }
      );
    }
  }, [open]);

  // fetch users based on search term and location
  const {
    data: usersData,
    isLoading,
    error,
  } = useQuery(
    ["membersData", currentLocation, searchTerm, conversationId, isSubConversation],
    async ({ signal }) => {
      if (!currentLocation || !conversationId) return { users: [] };

      const res = await axiosInstance.get(ApiEndPoints.CHAT.getNearbyPeople, {
        signal,
        params: {
          latitude: currentLocation.lat,
          longitude: currentLocation.lng,
          search: searchTerm || undefined,
          conversationId: conversationId, // This is now the correct ID (sub-conversation or main conversation)
        },
      });
      
      
      return res.data.data;
    },
    {
      enabled: !!currentLocation && !!conversationId && open,
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
    }
  );

  // filter out current members from the user list
  const availableUsers = (usersData?.users || []).filter(
    (user) => !currentMembers.some((member) => member._id === user._id)
  );

  // separate requested and available users
  const requestedUsers = availableUsers.filter(user => user.isRequested);
  const selectableUsers = availableUsers.filter(user => !user.isRequested);

  // handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // handle user selection/deselection
  const handleUserToggle = (user) => {

    setSelectedUsers((prev) => {
      const isSelected = prev.some((selected) => selected._id === user._id);


      if (isSelected) {
        const newSelection = prev.filter(
          (selected) => selected._id !== user._id
        );
        return newSelection;
      } else {
        const newSelection = [...prev, user];
        return newSelection;
      }
    });
  };

  // handle removing selected user
  const handleRemoveSelectedUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((user) => user._id !== userId));
  };

  // handle adding individual user
  const handleAddIndividualUser = async (user) => {
    try {
      
      
      if (isSubConversation && onAddMembersToSubConversation) {
        // Handle sub-conversation add individual user - use conversationId directly (which is sub-conversation ID)
        await onAddMembersToSubConversation([user], conversationId);
        toast.success(`${user.fullName} request sent   to sub-conversation`);
      } else {
        // Handle main conversation add individual user
        await onAddMembers([user], conversationId);
        toast.success(`${user.fullName} request sent  `);
      }
      
      onClose();
    } catch (error) {
      
      const errorMessage = isSubConversation 
        ? "failed to add user to sub-conversation. please try again."
        : "failed to add user. please try again.";
      
      toast.error(errorMessage);
    }
  };

  // handle opening confirmation dialog
  const handleOpenConfirmDialog = (user) => {
    setConfirmDialog({
      open: true,
      user: user,
    });
  };

  // handle closing confirmation dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      user: null,
    });
  };

  // handle deleting/canceling member request
  const handleDeleteMemberRequest = async () => {
    const { user } = confirmDialog;
    if (!user) return;

    try {
      
      
      const response = await axiosInstance.delete(
        `${ApiEndPoints.CONVERSATION.deleteMembersRequest}/${conversationId}`,
        {
          data: {
            userIds: [user._id],
          },
        }
      );

      if (response.data.data.success) {
        toast.success(`${user.fullName} request cancelled  `);
        // invalidate and refetch the members data
        queryClient.invalidateQueries(["membersData", currentLocation, searchTerm, conversationId, isSubConversation]);
        // close confirmation dialog
        handleCloseConfirmDialog();
      } else {
        throw new Error(response.data.message || "failed to cancel request");
      }
    } catch (error) {
      
      toast.error("failed to cancel request. please try again.");
    }
  };

  // handle adding selected members
  const handleAddMembers = async () => {
    if (selectedUsers.length === 0) {
      toast.error("please select at least one user to add");
      return;
    }

    try {
      if (isSubConversation && onAddMembersToSubConversation) {
        // Handle sub-conversation add members - use conversationId directly (which is sub-conversation ID)
        await onAddMembersToSubConversation(selectedUsers, conversationId);
      } else {
        // Handle main conversation add members
        await onAddMembers(selectedUsers, conversationId);
      }

      // reset state and close dialog
      setSelectedUsers([]);
      setSearchTerm("");
      onClose();

      const successMessage = isSubConversation 
        ? `${selectedUsers.length} member(s) request sent   to sub-conversation`
        : `${selectedUsers.length} member(s) request sent  `;
      
      toast.success(successMessage);
    } catch (error) {
      
      const errorMessage = isSubConversation 
        ? "failed to add members to sub-conversation. please try again."
        : "failed to add members. please try again.";
      
      toast.error(errorMessage);
    }
  };

  // handle dialog close
  const handleClose = () => {
    setSelectedUsers([]);
    setSearchTerm("");
    onClose();
  };

  // get user avatar
  const getUserAvatar = (user) => {
    if (user?.image?.profilePic) {
      
      return `${IMAGE_URL || ""}${user.image.profilePic}`;
    } else if (user?.image?.bannerImage) {
      return `${IMAGE_URL || ""}${user?.image?.bannerImage}`;
    }
    return null;
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">
              {isSubConversation ? "add members to sub-conversation" : "nearby people or beɘmz users"}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* search input */}
        <SearchContainer>
          <TextField
            fullWidth
            placeholder="search users..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
          />
        </SearchContainer>

        {/* selected users display */}
        {selectedUsers.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              selected users ({selectedUsers.length})
            </Typography>
            <SelectedUsersContainer>
              {selectedUsers.map((user) => (
                <Chip
                  key={user._id}
                  label={user.fullName}
                  onDelete={() => handleRemoveSelectedUser(user._id)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </SelectedUsersContainer>
          </Box>
        )}

        {/* users list */}
        <Typography variant="subtitle2" gutterBottom>
          available users ({selectableUsers.length})
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block" }}
        >
          click "add" to add individual users or select multiple users below
        </Typography>

        {/* debug info */}
        {selectedUsers.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            debug: {selectedUsers.length} users selected
          </Typography>
        )}

        <UserListContainer>
          {isLoading ? (
            <LoadingContainer>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                loading users...
              </Typography>
            </LoadingContainer>
          ) : error ? (
            <EmptyStateContainer>
              <Typography variant="body2" color="error">
                failed to load users. please try again.
              </Typography>
            </EmptyStateContainer>
          ) : availableUsers.length === 0 ? (
            <EmptyStateContainer>
              <Typography variant="body2">
                {searchTerm
                  ? "no users found matching your search"
                  : "no users available"}
              </Typography>
            </EmptyStateContainer>
          ) : (
            <>
              {/* Selectable users */}
              {selectableUsers.length > 0 && (
                <List dense>
                  {selectableUsers.map((user) => {
                const isSelected = selectedUsers.some(
                  (selected) => selected._id === user._id
                );

                return (
                  <ListItem
                    key={user._id}
                    onClick={() => {
                      if (!user.isRequested) {
                        handleUserToggle(user);
                      }
                    }}
                    sx={{
                      backgroundColor: isSelected
                        ? "action.selected"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: user.isRequested ? "transparent" : "action.hover",
                      },
                      cursor: user.isRequested ? "not-allowed" : "pointer",
                      opacity: user.isRequested ? 0.7 : 1,
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={user.isRequested}
                      onChange={() => {
                        if (!user.isRequested) {
                          handleUserToggle(user);
                        }
                      }}
                      color="primary"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <ListItemAvatar>
                      <Avatar src={getUserAvatar(user)} alt={user?.fullName}>
                        {user?.fullName.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">
                            {user.fullName}
                          </Typography>
                          {user.isRequested && (
                            <Typography
                              variant="caption"
                              sx={{
                                backgroundColor: "warning.light",
                                color: "warning.contrastText",
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: "0.65rem",
                              }}
                            >
                              pending
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        user?.userName ? `@${user?.userName}` : user?.email
                      }
                    />
                    <Button
                      variant="contained"
                      color={user.isRequested ? "secondary" : "primary"}
                      size="small"
                      startIcon={<AddIcon />}
                      disabled={user.isRequested}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!user.isRequested) {
                          handleAddIndividualUser(user);
                        }
                      }}
                      sx={{
                        minWidth: "auto",
                        px: 2,
                        py: 0.5,
                        fontSize: "0.75rem",
                        textTransform: "lowercase",
                        borderRadius: 2,
                        opacity: user.isRequested ? 0.6 : 1,
                      }}
                    >
                      {user.isRequested ? "requested" : "add"}
                    </Button>
                  </ListItem>
                );
              })}
            </List>
              )}

              {/* Requested users section */}
              {requestedUsers.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    pending requests ({requestedUsers.length})
                  </Typography>
                  <List dense>
                    {requestedUsers.map((user) => (
                      <ListItem
                        key={user._id}
                        sx={{
                          opacity: 0.7,
                          cursor: "not-allowed",
                        }}
                      >
                        <Checkbox
                          checked={false}
                          disabled={true}
                          color="primary"
                        />
                        <ListItemAvatar>
                          <Avatar src={getUserAvatar(user)} alt={user?.fullName}>
                            {user?.fullName.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2">
                                {user.fullName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  backgroundColor: "warning.light",
                                  color: "warning.contrastText",
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: "0.65rem",
                                }}
                              >
                                pending
                              </Typography>
                            </Box>
                          }
                          secondary={
                            user?.userName ? `@${user?.userName}` : user?.email
                          }
                        />
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenConfirmDialog(user);
                          }}
                          sx={{
                            minWidth: "auto",
                            px: 2,
                            py: 0.5,
                            fontSize: "0.75rem",
                            textTransform: "lowercase",
                            borderRadius: 2,
                          }}
                        >
                          cancel
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </>
          )}
        </UserListContainer>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleAddMembers}
          variant="contained"
          color="primary"
          disabled={selectedUsers.length === 0}
          startIcon={<PersonAddIcon />}
          sx={{ mt: 0.5 }}
        >
          add {selectedUsers.length > 0 ? `${selectedUsers.length} ` : ""}
          selected member
          {selectedUsers.length !== 1 ? "s" : ""}
        </Button>
      </DialogActions>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">cancel request</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar 
              src={confirmDialog.user ? getUserAvatar(confirmDialog.user) : null}
              alt={confirmDialog.user?.fullName}
            >
              {confirmDialog.user?.fullName?.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight="medium">
                {confirmDialog.user?.fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {confirmDialog.user?.userName ? `@${confirmDialog.user.userName}` : confirmDialog.user?.email}
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            are you sure you want to cancel the member request for{" "}
            <strong>{confirmDialog.user?.fullName}</strong>? this action cannot be undone.
          </Typography>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleCloseConfirmDialog} 
            color="inherit"
            variant="outlined"
          >
            keep request
          </Button>
          <Button
            onClick={handleDeleteMemberRequest}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            cancel request
          </Button>
        </DialogActions>
      </Dialog>
    </StyledDialog>
  );
};

export default AddMemberDialog;
